import { useCallback, useId, useMemo } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { Button } from '@/ui/Button/Button'
import { Textarea } from '@/ui/Field/Field'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { useEscapeShortcut } from '@/ui/overlay/escapeStack'
import { type Crumb, WsPageShell } from '../components/WsPageShell'
import { agentEditorCopy } from '../copy'
import { estimatePromptStats } from '../utils/promptTokens'
import type { AgentPromptOutletContext } from './AgentFormPage'

/**
 * Full-page system prompt editor, nested under the agent form so the form's
 * unsaved state stays mounted behind it. Edits land in the form; saving the
 * agent is still done back on the form page.
 */
export function AgentPromptPage() {
  const { agent, crumbs, parentUrl, prompt, setPrompt, dirty } =
    useOutletContext<AgentPromptOutletContext>()
  const navigate = useNavigate()
  const closePrompt = useCallback(
    () => void navigate(parentUrl, { replace: true }),
    [navigate, parentUrl],
  )
  useEscapeShortcut(closePrompt)
  const stats = useMemo(() => estimatePromptStats(prompt), [prompt])

  // The form page's last crumb is terminal there; here it becomes the way back.
  const parentCrumbs: Crumb[] = crumbs.map((c, i) =>
    i === crumbs.length - 1 ? { ...c, to: parentUrl } : c,
  )

  const titleId = useId()
  return (
    <WsPageShell
      titleId={titleId}
      crumbs={[...parentCrumbs, { label: agentEditorCopy.form.promptCrumb }]}
      title={
        agent
          ? agentEditorCopy.prompt.titleFor(agent.name)
          : agentEditorCopy.prompt.title
      }
      description={agentEditorCopy.prompt.description}
      actions={
        <Button href={parentUrl} replace>
          {agentEditorCopy.prompt.done}
        </Button>
      }
    >
      <Stack gap="space-12">
        <Inline justify="between" gap="space-8">
          <Text variant="caption" tone="text-mute" numeric>
            {agentEditorCopy.promptStats(stats.chars, stats.tokens)}
          </Text>
          {dirty && (
            <Text variant="caption" tone="text-mute">
              {agentEditorCopy.unsavedChanges}
            </Text>
          )}
        </Inline>
        <Textarea
          autoFocus
          aria-labelledby={titleId}
          rows={21}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
      </Stack>
    </WsPageShell>
  )
}
